import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FileWarning, Loader2, Pin, X } from "lucide-react";
import { fileType, getBlob, openPdf } from "../lib/attachments";
import { PdfPage } from "./AttachmentViewer";

const OPEN_DELAY = 300;
const GRACE = 350; // temps laissé pour aller de la miniature à l'aperçu
const W = 560;
const CLOSE_OTHERS = "norea:hover-preview-open";

function Body({ dossierId, file }) {
  const type = fileType(file);
  const [src, setSrc] = useState(null);
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    if (type === "image") getBlob(dossierId, file).then((b) => alive && setSrc(URL.createObjectURL(b))).catch(() => alive && setErr(true));
    if (type === "pdf") openPdf(dossierId, file).then((d) => alive && setDoc(d)).catch(() => alive && setErr(true));
    return () => { alive = false; };
  }, [dossierId, file, type]);
  if (type === "other" || err) return <div className="py-16 flex flex-col items-center gap-2 text-norea-textMuted text-sm"><FileWarning className="w-6 h-6" />Aperçu indisponible pour ce fichier</div>;
  if (type === "image") return src ? <img src={src} alt={file.filename} className="w-full h-auto block" draggable={false} /> : <Loader2 className="w-5 h-5 animate-spin m-10" />;
  if (!doc) return <Loader2 className="w-5 h-5 animate-spin m-10" />;
  return <div className="space-y-2">{Array.from({ length: doc.numPages }, (_, i) => <PdfPage key={i} doc={doc} n={i + 1} rot={0} width="100%" />)}</div>;
}

function place(rect) {
  const vw = window.innerWidth, vh = window.innerHeight;
  const h = Math.min(vh - 32, 820);
  const right = rect.right + 12 + W <= vw - 8;
  const left = right ? rect.right + 12 : Math.max(8, rect.left - 12 - W);
  const top = Math.min(Math.max(16, rect.top - 40), vh - h - 16);
  return { left, top, width: Math.min(W, vw - 16), height: h };
}

// Aperçu en grand au survol. L'aperçu reste ouvert quand la souris passe dessus (défilement possible) ;
// un clic l'épingle (il reste ouvert), la croix ou Échap le ferment.
export default function HoverPreview({ dossierId, file, children, className = "", testid = "hover-preview" }) {
  const trigger = useRef(null);
  const timers = useRef({});
  const id = useRef(Math.random().toString(36).slice(2));
  const [pos, setPos] = useState(null);
  const [pinned, setPinned] = useState(false);
  const clear = () => { clearTimeout(timers.current.open); clearTimeout(timers.current.close); };
  const close = useCallback(() => { clear(); setPos(null); setPinned(false); }, []);
  const enter = () => {
    clear();
    if (pos) return;
    timers.current.open = setTimeout(() => {
      window.dispatchEvent(new CustomEvent(CLOSE_OTHERS, { detail: id.current }));
      setPos(place(trigger.current.getBoundingClientRect()));
    }, OPEN_DELAY);
  };
  const leave = () => { clearTimeout(timers.current.open); if (!pinned) timers.current.close = setTimeout(close, GRACE); };
  useEffect(() => {
    if (!pos) return undefined;
    const key = (e) => e.key === "Escape" && close();
    const other = (e) => e.detail !== id.current && !pinned && close();
    window.addEventListener("keydown", key);
    window.addEventListener(CLOSE_OTHERS, other);
    return () => { window.removeEventListener("keydown", key); window.removeEventListener(CLOSE_OTHERS, other); };
  }, [pos, pinned, close]);
  useEffect(() => () => clear(), []);
  return (
    <div ref={trigger} onMouseEnter={enter} onMouseLeave={leave} className={className}>
      {children}
      {pos && createPortal(
        <div className="fixed z-[70] bg-white rounded-lg shadow-2xl border border-norea-border flex flex-col" style={pos} onMouseEnter={() => clearTimeout(timers.current.close)} onMouseLeave={leave}
          onClick={() => setPinned(true)} data-testid={testid}>
          <div className="flex items-center gap-2 px-3 py-2 border-b border-norea-border text-xs">
            <span className="truncate flex-1 font-medium" title={file.filename}>{file.label ? `${file.label} · ` : ""}{file.filename}</span>
            {pinned ? <span className="inline-flex items-center gap-1 text-norea-primary" data-testid={`${testid}-pinned`}><Pin className="w-3 h-3" />Épinglé</span>
              : <span className="text-norea-textMuted">Cliquez pour épingler</span>}
            <button onClick={(e) => { e.stopPropagation(); close(); }} className="p-1 rounded hover:bg-norea-surfaceHover" data-testid={`${testid}-close`}><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto overscroll-contain bg-norea-bg p-2" data-testid={`${testid}-scroll`}><Body dossierId={dossierId} file={file} /></div>
        </div>, document.body)}
    </div>
  );
}
